import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg, api } from "@/lib/api";

export default function Landing() {
  const { user, character, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && character) nav("/room");
    else if (user && !character) nav("/create");
  }, [user, character, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        await register(email, password, name);
        toast.success("Account don land! 🎉 Time to create your Sim.");
        nav("/create");
      } else {
        await login(email, password);
        toast.success("Welcome back, baller.");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full flex justify-center bg-gradient-to-b from-sky-100 to-slate-200">
      <div className="relative w-full max-w-md min-h-[100dvh] bg-[#F8FAFC] border-x border-slate-200 overflow-hidden">
        {/* glow blobs */}
        <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-[#FF0055]/20 blur-[90px]" />
        <div className="absolute top-40 -left-20 w-56 h-56 rounded-full bg-[#16A34A]/15 blur-[90px]" />

        <div className="relative z-10 px-6 pt-14 pb-10">
          <div className="flex items-center gap-2 mb-10">
            <Flame className="text-[#FF0055]" size={26} />
            <span className="label-xs text-slate-500">Lagos · Abuja</span>
            <button data-testid="advertise-with-us" onClick={() => nav("/promote")} className="ml-auto label-xs text-amber-700 border border-amber-300 bg-amber-50 rounded-full px-3 py-1 font-bold">Advertise With Us</button>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 120 }}
            className="font-display text-5xl leading-[0.95] text-slate-900"
          >
            HOT <span className="text-[#16A34A] neon-text">LIFE</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="mt-4 text-slate-500 text-base leading-relaxed"
          >
            Hustle, flex, fall in love and survive NEPA. Your Lagos story starts with one roll of the dice — <span className="text-[#FFD700]">rich kid or street hustler?</span>
          </motion.p>

          {stats && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              data-testid="live-stats" className="mt-5 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 bg-[#16A34A]/10 border border-[#16A34A]/30 text-[#15803D] rounded-full px-3 py-1.5 label-xs font-bold">
                <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" /> {stats.hustlers.toLocaleString()} hustlers playing · 100% free
              </span>
              {stats.ad_slots_open > 0 && (
                <button data-testid="landing-adslots" onClick={() => nav("/promote")} className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300 text-amber-700 rounded-full px-3 py-1.5 label-xs font-bold">
                  📣 {stats.ad_slots_open} billboard spots open
                </button>
              )}
            </motion.div>
          )}

          <div className="mt-8 flex gap-2 bg-white border border-slate-200 rounded-full p-1">
            {["register", "login"].map((m) => (
              <button
                key={m}
                data-testid={`tab-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 rounded-full label-xs transition-colors ${
                  mode === m ? "bg-[#16A34A] text-white" : "text-slate-500"
                }`}
              >
                {m === "register" ? "Sign Up" : "Log In"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-3">
            {mode === "register" && (
              <input
                data-testid="input-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Display name"
                required
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-slate-900 placeholder-zinc-600 focus:border-[#16A34A] focus:outline-none focus:ring-1 focus:ring-[#16A34A]"
              />
            )}
            <input
              data-testid="input-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-slate-900 placeholder-zinc-600 focus:border-[#16A34A] focus:outline-none focus:ring-1 focus:ring-[#16A34A]"
            />
            <input
              data-testid="input-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-slate-900 placeholder-zinc-600 focus:border-[#16A34A] focus:outline-none focus:ring-1 focus:ring-[#16A34A]"
            />
            <button
              data-testid="submit-auth"
              disabled={busy}
              className="w-full bg-[#FF0055] text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#ff2e73] transition-colors disabled:opacity-50"
            >
              {busy ? "Hold on…" : mode === "register" ? "Enter the City" : "Log In"}
              <ArrowRight size={18} />
            </button>
          </form>

          {/* Continue with Google (Emergent-managed OAuth) */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <button
            type="button"
            data-testid="google-signin"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/room";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-white border border-slate-300 text-slate-700 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-3 hover:bg-slate-50 transition-colors"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>

          <p className="mt-8 text-xs text-slate-400 leading-relaxed">
            No pay-to-win. Server-authoritative economy. Rent dey, tax dey, area boys dey. Play responsibly, no let NEPA catch you unprepared. ⚡
          </p>
        </div>
      </div>
    </div>
  );
}
